import React from 'react';

import { Tooltip, Radio } from '@mui/material';
import dayjs from 'dayjs';
import { Row, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MdInfo } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import { Button } from '../../../components';
import { generateReport, getReportData, removeReportData } from '../operations';
import { customStyles, formatSocial } from '../utils';

import './styles.scoped.css';

export const WorkerCompensationForm = ({
  form,
  setForm,
  branchOptions,
  WCCodeOptions,
  WCStateOptions,
  isLoadingBranches,
  isLoadingWCCodes,
  isLoadingWCStates,
  setLoadingResults,
  setData,
  setShowResults,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleViewResults = async () => {
    const checkRangeUsed = !!form?.ToCheckdate && !!form?.FromCheckdate;
    const periodRangeUsed = !!form?.ToPerEnddate && !!form?.FromPerEnddate;
    if (!checkRangeUsed && !periodRangeUsed) {
      alert('Please select either Check Date or Period End Date range.');
      return;
    }
    if (
      checkRangeUsed &&
      !(dayjs(form?.ToCheckdate).isAfter(form?.FromCheckdate) || dayjs(form?.FromCheckdate).isSame(form?.ToCheckdate))
    ) {
      alert('Invalid Check Date range. Please double check and try again.');
      return;
    }
    if (
      periodRangeUsed &&
      !(
        dayjs(form?.ToPerEnddate).isAfter(form?.FromPerEnddate) ||
        dayjs(form?.FromPerEnddate).isSame(form?.ToPerEnddate)
      )
    ) {
      alert('Invalid Period End Date range. Please double check and try again.');
      return;
    }
    setLoadingResults(true);
    try {
      const response = await generateReport({
        endpoint: 'worker-comp-report',
        payload: form,
      });
      if (response.location && response.location !== '') {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);
          setLoadingResults(false);
          setData(reportData);
          setShowResults(true);
        }
      } else {
        setLoadingResults(false);
        alert('No data found matching your criteria.');
      }
    } catch (error) {
      alert('No data found matching your criteria.');
      setLoadingResults(false);
    }
    setLoadingResults(false);
  };

  return (
    <div className="filters-container">
      <div className="filter-row">
        <Form.Group as={Row} style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>Corporation #</Form.Label>
          <Form.Label style={{ width: '290px' }}>{t('edge_reporting.worker_compensation.corporation')}</Form.Label>
        </Form.Group>
      </div>
      <div className="filter-row">
        <Form.Group as={Row} style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>Branch</Form.Label>
          <Select
            isDisabled={isLoadingBranches}
            isMulti
            closeMenuOnSelect={false}
            styles={customStyles('365px')}
            options={branchOptions}
            value={form?.Branch?.split(',').map(code => branchOptions.find(option => String(option.value) === code))}
            onChange={selectedOptions => {
              const selectedCodes = selectedOptions.map(option => option.value);
              setForm({
                ...form,
                Branch: selectedCodes.join(','),
              });
            }}
          />
          {isLoadingBranches && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              aria-hidden="true"
              className="dropdown-spinner"
            />
          )}
        </Form.Group>
      </div>

      <div
        className="filter-row"
        style={{
          marginTop: '30px',
        }}>
        <Form.Group as={Row} controlId="FromCheckdate" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>
            <div className="required">Check Date </div>
          </Form.Label>
          <Form.Control
            style={{
              width: '150px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            type="date"
            name="FromCheckdate"
            value={form?.FromCheckdate}
            onChange={handleChange}
            onBlur={null}
          />
        </Form.Group>
        <Form.Group as={Row} controlId="ToCheckdate" style={{ alignItems: 'center' }}>
          <Form.Label
            style={{
              width: '20px',
              marginLeft: '30px',
              marginRight: '20px',
            }}>
            To
          </Form.Label>
          <Form.Control
            style={{
              width: '150px',
              height: '30px',
            }}
            as="input"
            type="date"
            name="ToCheckdate"
            value={form?.ToCheckdate}
            onChange={handleChange}
            onBlur={null}
          />
        </Form.Group>
      </div>
      <div className="filter-row">
        <b>OR</b>
      </div>
      <div
        className="filter-row"
        style={{
          marginTop: '30px',
        }}>
        <Form.Group as={Row} controlId="FromPerEnddate" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>
            <div className="required">Period End Date </div>
          </Form.Label>
          <Form.Control
            style={{
              width: '150px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            type="date"
            name="FromPerEnddate"
            value={form?.FromPerEnddate}
            onChange={handleChange}
            onBlur={null}
          />
        </Form.Group>
        <Form.Group as={Row} controlId="ToPerEnddate" style={{ alignItems: 'center' }}>
          <Form.Label
            style={{
              width: '20px',
              marginLeft: '30px',
              marginRight: '20px',
            }}>
            To
          </Form.Label>
          <Form.Control
            style={{
              width: '150px',
              height: '30px',
            }}
            as="input"
            type="date"
            name="ToPerEnddate"
            value={form?.ToPerEnddate}
            onChange={handleChange}
            onBlur={null}
          />
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>Work Comp Code</Form.Label>
          <Select
            isDisabled={isLoadingWCCodes}
            isMulti
            closeMenuOnSelect={false}
            styles={customStyles('365px')}
            options={WCCodeOptions}
            defaultValue={form?.CompCode?.split(',').map(code => WCCodeOptions.find(option => option.value === code))}
            onChange={selectedOptions => {
              const selectedCodes = selectedOptions.map(option => option.value);
              setForm({
                ...form,
                CompCode: selectedCodes.join(','),
              });
            }}
          />
          {isLoadingWCCodes && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              aria-hidden="true"
              className="dropdown-spinner"
            />
          )}
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>Work Comp State</Form.Label>
          <Select
            isDisabled={isLoadingWCStates}
            isMulti
            closeMenuOnSelect={false}
            styles={customStyles('365px')}
            options={WCStateOptions}
            defaultValue={form?.CompState?.split(',').map(code => WCStateOptions.find(option => option.value === code))}
            onChange={selectedOptions => {
              const selectedCodes = selectedOptions.map(option => option.value);
              setForm({
                ...form,
                CompState: selectedCodes.join(','),
              });
            }}
          />
          {isLoadingWCStates && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              aria-hidden="true"
              className="dropdown-spinner"
            />
          )}
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} controlId="EmplLastName" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>{t('edge_reporting.worker_compensation.driver')} Last Name</Form.Label>
          <Form.Control
            style={{
              width: '360px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            name="EmplLastName"
            value={form?.EmplLastName}
            onChange={handleChange}
          />
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} controlId="EmplFirstName" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>
            {t('edge_reporting.worker_compensation.driver')} First Name
          </Form.Label>
          <Form.Control
            style={{
              width: '360px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            name="EmplFirstName"
            value={form?.EmplFirstName}
            onChange={handleChange}
          />
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} controlId="EmpId" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>{t('edge_reporting.worker_compensation.driver')} Number</Form.Label>
          <Form.Control
            style={{
              width: '360px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            name="EmpId"
            value={form?.EmpId}
            type="number"
            onChange={handleChange}
          />
        </Form.Group>
      </div>

      <div className="filter-row">
        <Form.Group as={Row} controlId="SSNO" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>{t('edge_reporting.worker_compensation.driver')} SSN</Form.Label>
          <Form.Control
            style={{
              width: '360px',
              height: '30px',
              marginRight: '20px',
            }}
            as="input"
            name="SSNO"
            value={formatSocial(form?.SSNO || '')}
            onChange={handleChange}
          />
        </Form.Group>
      </div>
      <div className="filter-row">
        <Form.Group as={Row} controlId="ReportFormat" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '290px' }}>Report Type</Form.Label>
          <div className="dropdown-spinner-container">
            <Select
              styles={customStyles('360px')}
              options={[
                { label: 'Detail', value: 'Detail' },
                { label: 'Summary', value: 'Summary' },
              ]}
              onChange={(selectedOption, action) => {
                const _form = {
                  ...form,
                  ReportFormat: action.action === 'clear' ? '' : selectedOption.value,
                };
                setForm(_form);
              }}
              name="ReportFormat"
              value={{
                label: form?.ReportFormat,
                value: form?.ReportFormat,
              }}
            />
          </div>
        </Form.Group>
      </div>
      <div className="filter-row">
        <Form.Group as={Row} controlId="ReportType" name="ReportType" style={{ alignItems: 'center' }}>
          <Form.Label style={{ width: '280px' }}>
            Filter For
            <Tooltip
              aria-multiline
              title={
                <div>
                  Standard = Work Comp Amount is not zero.
                  <br />
                  All Wages = All Work Comp Amounts.
                  <br />
                  Exception = Work Comp Amounts equal zero.
                </div>
              }
              arrow
              style={{ marginLeft: 5, marginBottom: 3 }}>
              <MdInfo color="var(--primary)" />
            </Tooltip>
          </Form.Label>
          <span>
            <span className="radio-group">
              <Radio
                style={{
                  transform: 'scale(1)',
                }}
                className="radio-button"
                onChange={() =>
                  setForm({
                    ...form,
                    FilterBy: 'standard',
                  })
                }
                name="fliterStandard"
                checked={form?.FilterBy === 'standard'}
              />
              <span>Standard</span>
            </span>
            <span className="radio-group">
              <Radio
                style={{
                  transform: 'scale(1)',
                  marginLeft: 30,
                }}
                className="radio-button"
                onChange={() =>
                  setForm({
                    ...form,
                    FilterBy: 'allWages',
                  })
                }
                name="fliterAllWages"
                checked={form?.FilterBy === 'allWages'}
              />
              <span>All Wages</span>
            </span>
            <span className="radio-group">
              <Radio
                style={{
                  transform: 'scale(1)',
                  marginLeft: 30,
                }}
                className="radio-button"
                onChange={() =>
                  setForm({
                    ...form,
                    FilterBy: 'exception',
                  })
                }
                name="fliterException"
                checked={form?.FilterBy === 'exception'}
              />
              <span>Exception</span>
            </span>
          </span>
        </Form.Group>
      </div>
      <div className="buttons-row">
        <Button
          variant="primary"
          onPress={() => {
            handleViewResults();
          }}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </div>
  );
};
